import role9MfrsSchema from '../../../../assets/forms/role-210100-mfrs.form.json';
import role9MpersSchema from '../../../../assets/forms/role-210100-mpers.form.json';
import { pickSchema, MPERS_ACCOUNTING_STANDARD } from './schema-picker.util';
import { isEmptyCell } from '../validation-field-key.util';
import { SHEET_KEYS } from '../../../models/sheet-keys';
import { resolveDimensionVisibility } from './dimension-visibility.util';
import { getDimensionVisibilityConfig } from './dimension-visibility-registry';

/**
 * Shared business rules for Role 9 (Sub-classification of Assets, Liabilities
 * and Equity).
 *
 * Role 9 is the third lifted sheet whose schema is **accounting-standard
 * dependent** (after roles 20 and 23). Two sibling form JSONs ship —
 * role-210100-mfrs.form.json and role-210100-mpers.form.json — that differ in
 * qname prefixes (ifrs-full / ssmt-mfrs vs ifrs-smes / ssmt-mpers) and in
 * which validation.equality blocks they declare. The component picks between
 * them at mount time based on the filing's accounting standard; the pure
 * validator has to make the same selection from
 * SheetValidationContext.accountingStandard.
 *
 * Both the editor's role9-210100-sub-classification.component.ts AND
 * role9SubClassificationValidator import from this file so the selection
 * literal and shared helpers live in exactly one place.
 *
 * ---- Cross-sheet dependency (role 8) ------------------------------------
 *
 * Role 9's MFRS schema declares ONE cross-sheet equality validation block
 * (on `ifrs-full:BiologicalAssets`) whose `sum` operation references two
 * fields on `statementOfFinancialPosition`:
 *
 *     ssmt-mfrs:CurrentBiologicalAssets
 *   + ssmt-mfrs:NoncurrentBiologicalAssets
 *
 * The editor's `runEqualityValidations()` runs through
 * `EqualityValidationService.validateAllEqualityRules`, which fans out by
 * column (Consolidated/Separate × Current/Previous) and reads role 8's
 * payload from `stateService.exportAllData()`. The pure path receives the
 * same role 8 slice through `context.crossSheetData?.get('statementOfFinancialPosition')`
 * — the orchestrator's `SheetDependencyService` already lists role 9 as
 * dependent on role 8, so the bundle is in scope.
 *
 * MPERS variant declares NO equality blocks today, so the cross-sheet read
 * silently no-ops for MPERS conversions (matching the editor).
 *
 * ---- hasErrors derivation -----------------------------------------------
 *
 * Role 9's `evaluateSheetStatus()` walks each row and flips `hasErrors=true`
 * iff any row's `getRowValidationStatus()` returns `'Failed'`. Failed
 * triggers when ALL of these are absent for a row:
 *
 *   1. A FormControl with `Validators.required` that is empty — but
 *      `buildForm()` attaches NO validators at all (see component L405-447),
 *      so this branch is never reached.
 *   2. A schema-level `control.required === true` whose row has no value —
 *      both schemas declare zero such controls today; latent-bug guard
 *      below covers any future schema edit.
 *   3. A non-"extracted sum" entry in the validationErrors Map — populated
 *      ONLY by `runEqualityValidations()`; API-mismatch warnings (which
 *      do contain "extracted sum") are filtered out and bucketed as
 *      Validated*, NOT Failed.
 *
 * Net effect: today, role 9's `hasErrors` is driven ENTIRELY by equality
 * validation results. The pure validator therefore mirrors only:
 *
 *   - Static required checks (latent-bug guard for future schemas).
 *   - Cross-sheet equality validation for any control with
 *     `validation.equality` whose every rule has `operation: 'sum'`.
 *
 * `hasSuggestedChanges` is always `false` because the editor's
 * `evaluateSheetStatus()` only writes `hasErrors` — API-mismatch warnings
 * never flow through `updateSheetLevelStatus`.
 */

/** Literal selecting the MPERS schema. Anything else falls back to MFRS. */
export const ROLE9_MPERS_ACCOUNTING_STANDARD = MPERS_ACCOUNTING_STANDARD;

/** Filename of the MFRS (default) variant schema. */
export const ROLE9_MFRS_SCHEMA_FILE = 'role-210100-mfrs.form.json';

/** Filename of the MPERS variant schema. */
export const ROLE9_MPERS_SCHEMA_FILE = 'role-210100-mpers.form.json';

/**
 * Column-visibility gating for Role 9 now lives in the shared, config-driven
 * resolver: see `dimension-visibility.util.ts` (`resolveDimensionVisibility`)
 * and the central `dimension-visibility-registry.ts`
 * (`SHEET_KEYS.subClassificationOfAssetsLiabilitiesEquity` → NATURE / PREP
 * gates). Both the mounted editor (via `CrossReferenceService.getVisibility`)
 * and the pure `role9SubClassificationValidator` (through
 * `resolveRole9VisibleColumns` below) call that one resolver, so the former
 * role-local `ROLE9_NATURE_*` / `ROLE9_PREPARATION_*` constants were removed to
 * keep a single source. The old inline default and the shared resolver agree
 * on all reachable inputs: the prep field only ever holds the two known
 * literals ("First time …" / "Subsequent …") or empty, and for each of those
 * three states both paths produce the SAME visible-column set.
 */

/**
 * Narrow shape of a role 9 schema control used by the pure validator. The
 * full schema carries far more metadata (ui, customValidations, calculated
 * flags etc.) but the validator only needs qname, required, validators, and
 * the equality block — keeping this type tight makes it resilient to
 * unrelated schema additions.
 */
export interface Role9EqualityRule {
  sheet: string;
  field: string;
  operation?: 'sum' | string;
  message?: string;
  tolerance?: number;
  taxonomy?: string;
  skipIfSourceNull?: boolean;
  skipIfTargetNull?: boolean;
}

export interface Role9SchemaControl {
  qname: string;
  type?: string;
  required?: boolean;
  validators?: Array<{ name?: string }>;
  ui?: {
    calculated?: boolean;
    readonly?: boolean;
    dualColumn?: boolean;
  };
  validation?: {
    equality?: Role9EqualityRule[];
  };
}

export interface Role9Schema {
  role: string;
  sheetId: string;
  label: string;
  taxonomy?: string; // 'FS-MFRS' | 'FS-MPERS'
  controls: Role9SchemaControl[];
}

/**
 * Resolve the correct role 9 schema filename for the given accounting
 * standard. Mirrors the component's loadSchema() selection so both paths
 * derive from the same rule.
 */
export function pickRole9SchemaFile(
  accountingStandard: string | undefined | null
): string {
  // Standard MFRS-default convention: MPERS only on the exact shared MPERS
  // literal, MFRS for everything else (incl. undefined/'').
  return pickSchema(
    accountingStandard,
    ROLE9_MFRS_SCHEMA_FILE,
    ROLE9_MPERS_SCHEMA_FILE
  );
}

/**
 * Resolve the actual role 9 schema object for the given accounting
 * standard. Used by the pure validator which cannot fetch() at validate-time
 * (the listing/dashboard's cache-miss path runs synchronously) so both
 * variants are imported eagerly and selected from memory.
 */
export function pickRole9Schema(
  accountingStandard: string | undefined | null
): Role9Schema {
  // Standard MFRS-default convention: MFRS for anything that is not the exact
  // shared MPERS literal.
  return pickSchema(
    accountingStandard,
    role9MfrsSchema as unknown as Role9Schema,
    role9MpersSchema as unknown as Role9Schema
  );
}

/** Extract MFRS|MPERS from the schema's `taxonomy` field. */
export function extractRole9SchemaTaxonomy(
  schema: Role9Schema
): 'MFRS' | 'MPERS' | null {
  const taxonomy = schema.taxonomy;
  if (!taxonomy) return null;
  if (taxonomy.includes('MFRS')) return 'MFRS';
  if (taxonomy.includes('MPERS')) return 'MPERS';
  return null;
}

/** Map the accounting-standard literal to the equivalent equality taxonomy. */
export function accountingStandardToTaxonomy(
  accountingStandard: string | undefined | null
): 'MFRS' | 'MPERS' {
  return accountingStandard === ROLE9_MPERS_ACCOUNTING_STANDARD
    ? 'MPERS'
    : 'MFRS';
}

export type Role9Dimension = 'Consolidated' | 'Separate';
export type Role9Period = 'CurrentYear' | 'PreviousYear';

/**
 * Build the bracket-suffixed key the editor stores its dual-column values
 * under (e.g. "ifrs-full:BiologicalAssets[Consolidated][CurrentYear]"). This
 * is exactly the form `validation-field-key.util's buildFieldKey` produces for
 * sheets registered in SHEET_COLUMN_TYPE_MAP as `'dualColumn'` — both role 9
 * and role 8 are dualColumn, so the same key shape applies on both sides.
 */
export function buildRole9DualColumnKey(
  qname: string,
  dimension: Role9Dimension,
  period: Role9Period
): string {
  return `${qname}[${dimension}][${period}]`;
}

/**
 * Resolve the visible (dimension × period) tuples for the current filing,
 * matching the mounted editor's column visibility (and so
 * ValidationContextService.getVisibleColumnSuffixes()).
 *
 * Now delegates to the shared, config-driven resolver
 * (`resolveDimensionVisibility` + the central registry's
 * `SHEET_KEYS.subClassificationOfAssetsLiabilitiesEquity` → NATURE / PREP
 * gates) and filters down to the cells the resolver marks visible. The shared
 * resolver is prereq-first with a filingInfo fallback, so the validator passes
 * BOTH slices. Its unknown/empty defaults (Nature unknown → Consolidated
 * shown; prep unknown → PreviousYear shown) match the broad pre-load default
 * the old inline logic used; for the only reachable prep values ("First time …"
 * / "Subsequent …" / empty) the two implementations produce identical column
 * sets, so this is a pure refactor.
 */
export function resolveRole9VisibleColumns(slices: {
  filingPrerequisites?: Record<string, unknown>;
  filingInfo?: Record<string, unknown>;
}): Array<{ dimension: Role9Dimension; period: Role9Period }> {
  const config = getDimensionVisibilityConfig(
    SHEET_KEYS.subClassificationOfAssetsLiabilitiesEquity
  )!;
  return resolveDimensionVisibility(config, slices)
    .filter((cell) => cell.visible)
    .map((cell) => ({ dimension: cell.dimension, period: cell.period }));
}

/**
 * Mirrors the component's getValidators()-style require check (from the
 * latent-bug fix pattern in roles 1, 2, 6): a control is statically
 * required when `required === true` OR when its `validators` array
 * contains an unconditional `{ name: 'required' }` entry. Both schemas
 * declare zero such controls today; this helper exists as a defensive
 * guard so future schema edits get picked up automatically.
 */
export function isRole9StaticallyRequired(control: Role9SchemaControl): boolean {
  if (control.required === true) return true;
  return Array.isArray(control.validators)
    ? control.validators.some((validator) => validator?.name === 'required')
    : false;
}

/**
 * Empty-value check matching the component's null/undefined/'' guards.
 * Delegates to the shared `isEmptyCell` so every sheet shares one definition.
 */
export function isRole9Empty(value: unknown): boolean {
  return isEmptyCell(value);
}
